/**
 * Aggregation Service
 * Handles portfolio aggregations and distribution analysis API calls
 */

import httpClient, { extractData } from './http-client';
import {
  ApiResponse,
  HistoricalAggregatedMetrics,
  HistoricalDistributionPoint,
  AllBreakdowns,
  Period,
  Dimension,
} from '../types';

class AggregationService {
  /**
   * Get historical aggregated portfolio metrics (weighted Greeks, premium, vol, etc.) from backend.
   * No local fallback - returns backend data only.
   */
  async getAggregatedMetrics(
    period: Period = 'MTD'
  ): Promise<HistoricalAggregatedMetrics[]> {
    const response = await httpClient.get<ApiResponse<HistoricalAggregatedMetrics[]>>(
      '/bonds/aggregations/metrics',
      { params: { period } }
    );
    return extractData(response);
  }

  /**
   * Get historical distribution by dimension (backend-only).
   * Returns array of { date, distributions } for stacked area chart.
   */
  async getDistribution(
    dimension: Dimension,
    period: Period = 'YTD'
  ): Promise<HistoricalDistributionPoint[]> {
    const response = await httpClient.get<ApiResponse<HistoricalDistributionPoint[]>>(
      '/bonds/aggregations/distribution',
      { params: { dimension, period } }
    );
    return extractData(response);
  }

  /**
   * Get cross-filter data for multi-dimensional analysis (backend-only).
   * Returns { primary, secondary, matrix, totalBonds, totalNotional }.
   */
  async getCrossFilter(
    primary: string,
    secondary?: string
  ): Promise<{
    primary: Array<{
      name: string;
      count: number;
      cbMarketCap: number;
      equityMarketCap: number;
      avgDelta?: number;
      avgPrime?: number;
      avgYTM?: number;
      avgDuration?: number;
      [key: string]: any;
    }>;
    secondary: any[];
    matrix: Record<string, Record<string, { count: number; totalNotional: number }>>;
    totalBonds: number;
    totalNotional: number;
  }> {
    const response = await httpClient.get<ApiResponse<any>>(
      '/bonds/aggregations/cross-filter',
      { params: { primary, secondary } }
    );
    return extractData(response);
  }

  /**
   * Get all breakdowns at global max date (VBA methodology) - backend-only.
   */
  async getBreakdowns(): Promise<AllBreakdowns> {
    const response = await httpClient.get<ApiResponse<AllBreakdowns>>(
      '/bonds/aggregations/breakdowns'
    );
    return extractData(response);
  }

  /**
   * Get aggregated performance YTD by primary dimension at global J - backend-only.
   */
  async getPerformanceByPrimary(
    primary: 'sector' | 'rating' | 'size' | 'profile' | 'maturity'
  ): Promise<Array<{ name: string; performance: number; percentage: number; count: number; marketCap: number }>> {
    const response = await httpClient.get<ApiResponse<any>>(
      '/bonds/aggregations/performance-by-primary',
      { params: { primary } }
    );
    return extractData(response);
  }
}

export const aggregationService = new AggregationService();
export default aggregationService;
